// Client mirror of the server's pricing strategies (the `rule_type` of a
// pricing rule — see server/services/pricing-strategies.js). The server is
// authoritative and validates every write; this copy lets the admin UI label
// strategies, seed editor defaults, and split the pricing matrix into a section
// per strategy. Keep in sync with the server if strategies are added.

export const PRICING_STRATEGIES = Object.freeze({
  TIERED: 'per_sqmi_tiered',
  FLAT: 'flat',
  CUSTOM_QUOTE: 'custom_quote',
});

// New targets with no rule yet fall back to the historical tiered model.
export const DEFAULT_STRATEGY = PRICING_STRATEGIES.TIERED;

export const STRATEGY_LABELS = Object.freeze({
  [PRICING_STRATEGIES.TIERED]: 'Per square mile (tiered)',
  [PRICING_STRATEGIES.FLAT]: 'Flat price',
  [PRICING_STRATEGIES.CUSTOM_QUOTE]: 'Custom quote',
});

export const STRATEGY_HELP = Object.freeze({
  [PRICING_STRATEGIES.TIERED]: 'Charge varies by area size (rate per square mile).',
  [PRICING_STRATEGIES.FLAT]: 'A single fixed price, the same for all area sizes.',
  [PRICING_STRATEGIES.CUSTOM_QUOTE]: 'Always priced manually — no automated estimate. Selecting it asks the customer for a custom quote.',
});

// Order shown in selectors.
export const STRATEGY_OPTIONS = [
  { value: PRICING_STRATEGIES.TIERED, label: STRATEGY_LABELS[PRICING_STRATEGIES.TIERED] },
  { value: PRICING_STRATEGIES.FLAT, label: STRATEGY_LABELS[PRICING_STRATEGIES.FLAT] },
  {
    value: PRICING_STRATEGIES.CUSTOM_QUOTE,
    label: STRATEGY_LABELS[PRICING_STRATEGIES.CUSTOM_QUOTE],
  },
];

export const isValidStrategy = (t) => Object.values(PRICING_STRATEGIES).includes(t);

// True for the always-manual custom-quote strategy (no editable rate values).
export const isCustomQuoteStrategy = (t) => t === PRICING_STRATEGIES.CUSTOM_QUOTE;

// Normalize a possibly-missing rule_type to a supported strategy.
export const strategyFromRule = (rule) => {
  const t = rule?.rule_type;
  return isValidStrategy(t) ? t : DEFAULT_STRATEGY;
};

// Read a flat rule's amount, defaulting to 0.
export const amountFromRule = (rule) => {
  const n = Number(rule?.payload?.amount);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
